/**
 * oraculoAudio.js
 *
 * Helpers compartidos para el catalogo de audios del Oraculo (Cerebro 2.0).
 * Usado por StockAnalysis.jsx (stocks) y Investments.jsx (portafolios).
 *
 * El catalogo se genera con scripts/generate_oraculo_audios.js en
 * flowexpay-investments-api y se sube a S3 en:
 *   audio/oraculo/{symbol_lower}/{tier}-es.mp3       (stocks)
 *   audio/portafolio/{portfolio_id}/{tier}-es.mp3    (portafolios)
 *
 * AVAILABLE_AUDIOS debe actualizarse manualmente despues de cada generacion
 * para reflejar que (item, tier) combinaciones existen en S3.
 */

// Base URL del bucket S3 (o CloudFront frente al bucket).
// Override con VITE_AUDIO_BASE_URL si migramos a R2 o a otro CDN sin tocar codigo.
export const AUDIO_BASE = import.meta.env.VITE_AUDIO_BASE_URL || 'https://beta.flowexpay.com/audio';

export const ALL_TIERS = ['basic', 'intermediate', 'advanced', 'premium'];

/**
 * Allowlist tier-aware. Solo combinaciones listadas aqui mostraran reproductor.
 * Catalogo completo generado May 23 2026 (76 audios).
 */
export const AVAILABLE_AUDIOS = {
  stocks: {
    AAPL: ALL_TIERS,
    MSTR: ALL_TIERS, // Strategy — 4 audios generados 25-ago-2026

    MSFT: ALL_TIERS,
    NVDA: ALL_TIERS,
    TSLA: ALL_TIERS,
    PLTR: ALL_TIERS,
    HIMS: ALL_TIERS,
    DUOL: ALL_TIERS,
    VOO: ALL_TIERS,
    QQQ: ALL_TIERS,
    BND: ALL_TIERS,
    GLD: ALL_TIERS,
    SCHD: ALL_TIERS,
    SOXX: ALL_TIERS,
    ARKK: ALL_TIERS,
    VWO: ALL_TIERS,
    // Acciones publicadas (audios ya generados y subidos a S3).
    SPCX: ALL_TIERS,
    CAI: ALL_TIERS,
    RXRX: ALL_TIERS,
    TEM: ALL_TIERS,
    NAUT: ALL_TIERS,
    IBRX: ALL_TIERS,
    INKT: ALL_TIERS,
    NGEN: ALL_TIERS,
    PBLS: ALL_TIERS,
    // Lote fabrica del Cerebro (4-jul-2026, pasada 2 con chunking). Inventario
    // REAL de S3. Faltan 16 audios (~100K creditos ElevenLabs): los premium de
    // las primeras del loop + advanced de varias — completar con la proxima
    // recarga o el ciclo del 23 de julio, relanzando el mismo loop.
    TSM: ALL_TIERS,
    V: ALL_TIERS,
    ORCL: ALL_TIERS,
    SHOP: ALL_TIERS,
    SOFI: ALL_TIERS,
    AMZN: ['basic', 'intermediate', 'advanced'],
    META: ['basic', 'intermediate', 'advanced'],
    AMD: ['basic', 'intermediate', 'advanced'],
    NU: ['basic', 'intermediate'],
    MELI: ['basic', 'intermediate'],
    GOOGL: ['basic', 'intermediate'],
    COIN: ['basic', 'intermediate'],
    BABA: ['basic', 'intermediate'],
    GLOB: ['basic'],
    // Acciones nuevas del Cerebro (27-jul-2026): las 8 más transadas que faltaban.
    // Audios generados con generate_oraculo_audios.js (voz Daniela) y subidos a S3.
    MU: ALL_TIERS,
    SNDK: ALL_TIERS,
    INTC: ALL_TIERS,
    NOW: ALL_TIERS,
    BAC: ALL_TIERS,
    NFLX: ALL_TIERS,
    VZ: ALL_TIERS,
    T: ALL_TIERS,
  },
  portfolios: {
    // Los 4 perfiles legacy: siguen sirviendo a quien aun no tiene portafolio
    // multimoneda asignado.
    guardian: ALL_TIERS,
    explorer: ALL_TIERS,
    visionary: ALL_TIERS,
    income_builder: ALL_TIERS,
    // Los 9 multimoneda. Solo PREMIUM: en Mi Plan el audio del portafolio
    // siempre es premium (decision May 24 2026), asi que generar los otros
    // tres tiers seria pagarle a ElevenLabs por audios que nadie oye.
    // Los 9 multimoneda, regenerados 3-ago-2026 con el guion corregido: sin
    // pesos viejos, sin siglas, sin jerga, con la explicacion de que aporta la
    // pata en pesos y su propio pre-mortem. Solo PREMIUM: en Mi Plan el audio
    // del portafolio siempre es premium.
    'guardian-raiz': ['premium'],
    'guardian-puente': ['premium'],
    'explorer-raiz': ['premium'],
    'explorer-puente': ['premium'],
    'explorer-global': ['premium'],
    'visionary-puente': ['premium'],
    'visionary-global': ['premium'],
    'income_builder-raiz': ['premium'],
    'income_builder-puente': ['premium'],
  },
};

/**
 * Metadata por tier para los labels del HeroAudioPlayer.
 */
export const TIER_AUDIO_META = {
  basic:        { label: 'Análisis básico',     subtitle: 'La tesis en una frase y una analogía para empezar' },
  intermediate: { label: 'Análisis intermedio', subtitle: 'Incluye el moat y el contexto macro' },
  advanced:     { label: 'Análisis avanzado',   subtitle: 'Curva de adopción, plataformas y bear case' },
  premium:      { label: 'Análisis premium',    subtitle: 'Estilo institucional — las 10 lentes del Oráculo' },
};

export const PORTFOLIO_TIER_META = {
  basic:        { label: 'Tu portafolio explicado',          subtitle: 'Filosofía y para quién es' },
  intermediate: { label: 'Tu portafolio en profundidad',     subtitle: 'Apuestas centrales y balance interno' },
  advanced:     { label: 'Tu portafolio en distintos escenarios', subtitle: 'Comportamiento macro y peor caída histórica' },
  premium:      { label: 'Tu portafolio sin filtros',        subtitle: 'Pre-mortem honesto, bear case y compañero mental' },
};

/**
 * URL del audio narrado para una accion en un tier especifico.
 * Returns null si no existe en el catalogo (allowlist).
 */
export function getStockAudioUrl(symbol, tier) {
  if (!symbol || !tier) return null;
  const sym = symbol.toUpperCase();
  const available = AVAILABLE_AUDIOS.stocks[sym] || [];
  if (!available.includes(tier)) return null;
  return `${AUDIO_BASE}/oraculo/${sym.toLowerCase()}/${tier}-es.mp3`;
}

/**
 * URL del audio narrado para un portafolio en un tier especifico.
 * portfolioId: 'guardian' | 'explorer' | 'visionary' | 'income_builder'
 */
export function getPortfolioAudioUrl(portfolioId, tier) {
  if (!portfolioId || !tier) return null;
  const id = portfolioId.toLowerCase();
  const available = AVAILABLE_AUDIOS.portfolios[id] || [];
  if (!available.includes(tier)) return null;
  return `${AUDIO_BASE}/portafolio/${id}/${tier}-es.mp3`;
}

/**
 * URL del audio mensual del statement para un arquetipo.
 * period: 'YYYY-MM', archetype: 'guardian' | 'explorer' | 'visionary' | 'income_builder'
 */
export function getStatementAudioUrl(period, archetype) {
  if (!period || !archetype) return null;
  return `${AUDIO_BASE}/statement/${period}/${archetype.toLowerCase()}-es.mp3`;
}
